import type { JsonObject, JsonValue } from '../json';
import { isRecord, isValidTimestamp } from '../validation';
import type {
	CreditDenial,
	CreditDenialScope,
	CreditRequestMethod,
	ReportedCreditDenial,
} from './types';

const KNOWN_DENIAL_SCOPES = new Set<CreditDenialScope>(['ORGANIZATION', 'USER', 'AGENT']);

// Ignore malformed optional dates without losing the backend's credit decision.
export const getResetDate = (value: JsonValue | undefined): number | undefined =>
	isValidTimestamp(value) && value > 0 && !Number.isNaN(new Date(value).getTime())
		? value
		: undefined;

// An unknown mode is treated as EMAIL, but a redirect without a usable web URL offers nothing.
const parseRequestMethod = (value: JsonValue | undefined): CreditRequestMethod | undefined => {
	if (!isRecord(value) || typeof value.mode !== 'string') {
		return undefined;
	}

	if (value.mode !== 'REDIRECT_URL') {
		return { mode: 'EMAIL' };
	}

	try {
		const { protocol, username, password } = new URL(String(value.redirectUrl));
		return protocol === 'https:' &&
			!username &&
			!password &&
			String(value.redirectUrl).length <= 2048
			? { mode: 'REDIRECT_URL', redirectUrl: String(value.redirectUrl) }
			: undefined;
	} catch {
		return undefined;
	}
};

export const parseCreditDenial = (denial: JsonObject): CreditDenial => {
	const scope = KNOWN_DENIAL_SCOPES.has(denial.scope as CreditDenialScope)
		? (denial.scope as CreditDenialScope)
		: 'UNKNOWN';
	const resetsAt = scope === 'UNKNOWN' ? undefined : getResetDate(denial.resetsAt);
	const requestMethod = scope === 'USER' ? parseRequestMethod(denial.requestMethod) : undefined;

	return {
		scope,
		...(resetsAt !== undefined ? { resetsAt } : {}),
		...(requestMethod !== undefined ? { requestMethod } : {}),
	};
};

/**
 * Applies the backend response rules to a denial reported by a host surface: an unrecognised scope
 * becomes `UNKNOWN`, an unusable reset date is dropped, and only a `USER` denial keeps a valid
 * request method. Returns null when the value is not an object, so a bad report is ignored. A
 * report without a recognised scope is not ignored: it becomes an `UNKNOWN` denial, which still
 * shows the generic block because the host only reports when AI usage was blocked.
 */
export const normalizeReportedCreditDenial = (
	denial: ReportedCreditDenial,
): CreditDenial | null => {
	if (!isRecord(denial)) {
		return null;
	}

	const { scope, resetsAt, requestMethod } = denial;
	return parseCreditDenial({
		scope,
		...(resetsAt !== undefined ? { resetsAt } : {}),
		...(requestMethod !== undefined ? { requestMethod } : {}),
	});
};
